/**
 * Account schemas — password policy, password-auth request shapes, and the
 * main/sub-account permission model (ADP-167).
 *
 * Password policy requires: 6-20 chars, at least 2 of
 * { letters, digits, symbols }. Whitespace never counts as a class.
 *
 * Permission model: sub-accounts carry a module-permission list + a shop
 * scope; the main account (workspace owner) implicitly has everything.
 */
import { z } from "zod";
import { EmailSchema, type PermissionCode } from "./auth";

const PASSWORD_CLASS_PATTERNS = [/[A-Za-z]/, /\d/, /[^A-Za-z0-9\s]/] as const;

export function passwordClassCount(password: string): number {
  return PASSWORD_CLASS_PATTERNS.filter((pattern) => pattern.test(password)).length;
}

export const PASSWORD_POLICY_MESSAGE = "长度为6-20个字符，必须包含字母，数字，符号中至少2种";

export const PasswordSchema = z
  .string()
  .min(6, PASSWORD_POLICY_MESSAGE)
  .max(20, PASSWORD_POLICY_MESSAGE)
  .refine((value) => !/\s/.test(value), PASSWORD_POLICY_MESSAGE)
  .refine((value) => passwordClassCount(value) >= 2, PASSWORD_POLICY_MESSAGE);

/** POST /auth/login */
export const loginRequestSchema = z.object({
  email: EmailSchema,
  // Login never validates the policy — legacy/imported credentials must still
  // authenticate even if they'd fail today's rules. The cap only bounds
  // scrypt's input-length cost; it is far above the 20-char policy max.
  password: z.string().min(1).max(128),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/** POST /auth/password/change */
export const changePasswordRequestSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: PasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

/** POST /auth/password/forgot */
export const forgotPasswordRequestSchema = z.object({
  email: EmailSchema,
});
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

/** POST /auth/password/reset */
export const resetPasswordRequestSchema = z.object({
  email: EmailSchema,
  code: z.string().length(6),
  newPassword: PasswordSchema,
});
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

// ─── Module permissions + shop scope (main/sub-account model) ──────

/**
 * Module keys map 1:1 to AdPraxis's top-level nav:
 *   dashboard → /(店铺总览)     campaigns → /campaigns + /portfolios
 *   ai        → /managed-groups
 *   approvals → /ai-operations   settings  → /settings
 *
 * Campaign-level AI 配置 lives inside the campaign detail page, so its PAGE
 * access follows `campaigns`; the save action is still gated by `manage_ai`
 * (derived from the `ai` module) on the server.
 */
export const ModuleKeySchema = z.enum(["dashboard", "campaigns", "ai", "approvals", "settings"]);
export type ModuleKey = z.infer<typeof ModuleKeySchema>;
export const ALL_MODULES: readonly ModuleKey[] = ModuleKeySchema.options;

/** Sub-account shop scope. `all` tracks the workspace's shops over time. */
export const ShopScopeSchema = z.union([
  z.object({ mode: z.literal("all") }),
  z.object({ mode: z.literal("selected"), shopIds: z.array(z.string().uuid()).min(1) }),
]);
export type ShopScope = z.infer<typeof ShopScopeSchema>;

/**
 * Effective permission codes for a member (ADP-167). Owner = main account =
 * full set; `admin` (backstage-promoted sub-account, ADP-180) gets the same
 * full set and ignores stored module grants. Plain members derive from their
 * module grants (null = all modules). `manage_team` is never derivable — only
 * owner/admin hold it.
 *
 * Boundary: `ai`/`approvals`/`settings` map to write-gating codes
 * (manage_ai / approve / manage_connections). The read-only modules
 * (`dashboard`/`campaigns`) are FRONTEND gating only — a sub-account without
 * the `campaigns` module can still call campaign read endpoints directly;
 * server-side isolation is by shop scope + write codes, not per-module read
 * surfaces.
 */
export function permissionsForMember(
  role: "owner" | "admin" | "member" | (string & {}),
  modules: ModuleKey[] | null | undefined,
): PermissionCode[] {
  if (isPrivilegedRole(role))
    return ["view", "manage_ai", "approve", "manage_team", "manage_connections"];
  const granted = modules ?? ALL_MODULES;
  const codes: PermissionCode[] = ["view"];
  if (granted.includes("ai")) codes.push("manage_ai");
  if (granted.includes("approvals")) codes.push("approve");
  // The settings module carries full workspace-settings semantics: a granted
  // sub-account can manage Amazon/SP-API connections, not just view the page.
  if (granted.includes("settings")) codes.push("manage_connections");
  return codes;
}

/** Owner or admin — full module permissions, all shops, manage_team (ADP-180). */
export function isPrivilegedRole(role: string | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

/** Whether a sub-account's shop scope covers a given shop id. */
export function shopScopeAllows(scope: ShopScope | null | undefined, shopId: string): boolean {
  if (!scope || scope.mode === "all") return true;
  return scope.shopIds.includes(shopId);
}

// ─── Sub-account management (owner-only /account/* endpoints) ──────

export const createSubAccountRequestSchema = z.object({
  name: z.string().trim().min(1).max(50),
  email: EmailSchema,
  password: PasswordSchema,
  modulePermissions: z.array(ModuleKeySchema).min(1),
  shopScope: ShopScopeSchema,
});
export type CreateSubAccountRequest = z.infer<typeof createSubAccountRequestSchema>;

/** Email and password are immutable in edit (Xnurta parity). */
export const updateSubAccountRequestSchema = z.object({
  name: z.string().trim().min(1).max(50).optional(),
  modulePermissions: z.array(ModuleKeySchema).min(1).optional(),
  shopScope: ShopScopeSchema.optional(),
});
export type UpdateSubAccountRequest = z.infer<typeof updateSubAccountRequestSchema>;

export const updateAccountProfileRequestSchema = z.object({
  name: z.string().trim().min(1).max(50).optional(),
  email: EmailSchema.optional(),
});
export type UpdateAccountProfileRequest = z.infer<typeof updateAccountProfileRequestSchema>;

export const subAccountSchema = z.object({
  userId: z.string().uuid(),
  name: z.string().nullable(),
  email: z.string().email(),
  status: z.enum(["active", "disabled"]),
  /** admin = backstage-promoted 管理员 (ADP-180); immutable from /account/*. */
  role: z.enum(["admin", "member"]),
  modulePermissions: z.array(ModuleKeySchema).nullable(),
  shopScope: ShopScopeSchema.nullable(),
  shopCount: z.number().int().nonnegative(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type SubAccount = z.infer<typeof subAccountSchema>;
